import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getProductsRequest, getShippingRequest, ResetShoppingcart, setRegion } from "../../redux/actions";
import { getFaqsRequest } from "../../redux/actions/faqs";
import { AppState } from "../../redux/types";
import { getPricingRegion } from "../../utils/constants";
import { isPurchaseFlowPath } from "../../utils/purchaseFlow";

interface PendingChange {
    regionCode: string;
    newLang: string;
    prevLang: string;
    newPath?: string;
}

const LanguageChangeDialog = () => {
    const [show, setShow] = useState(false);
    const pending = useRef<PendingChange | null>(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const currentRegion = useSelector((state: AppState) => state.region.code);

    const applyChange = useCallback((p: PendingChange) => {
        // Write localStorage only after confirmed
        if (p.newLang !== p.prevLang) {
            localStorage.setItem("_prevLang", p.prevLang);
            localStorage.setItem("language", p.newLang);
        }
        // User explicitly chose a region — dismiss the detection banner
        localStorage.setItem("regionBannerDismissed", "true");
        localStorage.setItem("region", p.regionCode);
        dispatch(setRegion(p.regionCode));
        dispatch(getProductsRequest());
        dispatch(getShippingRequest());
        dispatch(getFaqsRequest());
        // For language changes: full page navigation so Weglot proxy can
        // intercept and translate server-side. Replace the current history
        // entry so the browser Back button does not return to the same page
        // in the previous language with stale widget state.
        if (p.newPath && p.newLang !== p.prevLang) {
            window.location.replace(p.newPath);
        } else if (p.newPath) {
            history.push(p.newPath);
        }
    }, [dispatch, history]);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as PendingChange;
            const confirmedBuyRegion = sessionStorage.getItem("confirmedBuyRegion");
            const isPricingCountryChanging = getPricingRegion(detail.regionCode) !== getPricingRegion(currentRegion);
            if (isPurchaseFlowPath(window.location.pathname) && !!confirmedBuyRegion && isPricingCountryChanging) {
                pending.current = detail;
                setShow(true);
            } else {
                applyChange(detail);
            }
        };
        window.addEventListener("regionChangeRequested", handler);
        return () => window.removeEventListener("regionChangeRequested", handler);
    }, [currentRegion, applyChange]);

    const handleConfirm = () => {
        if (pending.current) {
            dispatch(ResetShoppingcart());
            applyChange(pending.current);
        }
        pending.current = null;
        setShow(false);
    };

    const handleCancel = () => {
        // Weglot was never switched — nothing to revert
        pending.current = null;
        setShow(false);
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static" keyboard={false}>
            <Modal.Header>
                <Modal.Title>Language / Region Change</Modal.Title>
            </Modal.Header>
            <Modal.Body>Changing the language or region will clear your shopping cart. Do you want to continue?</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirm}>Continue</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LanguageChangeDialog;
